import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { CalendarClock, ChevronDown, Clock, CreditCard, ExternalLink, Lock, Users, Video, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { mentorAPI } from '../lib/api';
import { t } from '../lib/i18n';
import { EmptyState, Field, Modal, btnGhost, btnPrimary, inputCls, panelCls } from '../components/ui';

// Mentor Calendar (LMS side). Shows ONLY the "LMS" slots of the member's assigned
// mentor; slots are created/managed by the mentor in My Account. Booking rules
// (capacity, waitlist, paid, credits, emails) are applied by the CMS — this page
// just asks and renders. Opened from the lesson player it carries ?course=&lesson=
// so the mentor sees which lesson the doubt is about.

const press = 'transition-transform duration-150 active:scale-[0.97]';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const dayLabel = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });

const money = (cents, currency) => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format((cents || 0) / 100);
  } catch {
    return `$${((cents || 0) / 100).toFixed(2)}`;
  }
};

const STATUS_TEXT = {
  upcoming: 'Upcoming',
  completed: 'Completed',
  waitlist: 'Waiting list',
  cancelled: 'Cancelled',
};

export default function MentorCalendar() {
  const [params] = useSearchParams();
  const location = useLocation();
  const courseId = params.get('course') || '';
  const lessonId = params.get('lesson') || '';
  const ctxTitles = location.state || {};

  const [cal, setCal] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(null); // slot being booked
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  // Sessions where *I* am the mentor (s150) — empty for a regular member.
  const [mine, setMine] = useState([]);

  const load = useCallback(() => {
    mentorAPI.mySessions().then((r) => setMine(r.data || [])).catch(() => setMine([]));
    Promise.all([mentorAPI.calendar(), mentorAPI.bookings()])
      .then(([c, b]) => {
        setCal(c.data);
        setBookings(b.data || []);
        setError('');
      })
      .catch((err) => setError(err.response?.data?.detail || t('Could not load the mentor calendar')));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const days = useMemo(() => {
    const today = todayStr();
    const groups = {};
    (cal?.slots || [])
      .filter((s) => (s.date || '') >= today)
      .sort((a, b) => `${a.date}${a.start_time}`.localeCompare(`${b.date}${b.start_time}`))
      .forEach((s) => {
        (groups[s.date] = groups[s.date] || []).push(s);
      });
    return Object.entries(groups);
  }, [cal]);

  const openBook = (slot) => {
    setNote('');
    setDialog(slot);
  };

  const confirmBook = async () => {
    if (!dialog) return;
    setBusy(true);
    try {
      const paid = cal.paid_enabled && (dialog.price_cents || 0) > 0;
      const { data } = await mentorAPI.book(dialog.id, {
        use_credit: paid,
        course_id: courseId,
        lesson_id: lessonId,
        note,
      });
      toast.success(data.status === 'booked' ? t('Session booked') : t('Added to the waiting list'));
      setDialog(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Booking failed'));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (slotId) => {
    if (!window.confirm(t('Cancel this booking?'))) return;
    try {
      await mentorAPI.cancel(slotId);
      toast.success(t('Booking cancelled'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Could not cancel'));
    }
  };

  if (error) return <EmptyState>{error}</EmptyState>;
  if (!cal) return <p className="text-sm text-lms-muted">{t('Loading…')}</p>;

  const header = (
    <div className="mb-5">
      <h1 className="text-lg font-semibold">{t('Mentor calendar')}</h1>
      <p className="mt-1 text-sm text-lms-muted">
        {cal.mentor
          ? `${t('Book a session with your mentor')} ${cal.mentor.name} ${t('to clear up any doubt about your courses.')}`
          : t('Book a session with your mentor to clear up any doubt about your courses.')}
      </p>
    </div>
  );

  const mentorBlock = mine.length > 0 && (
    <MentorSessions
      sessions={mine}
      onSaved={(id, pn) => setMine((prev) => prev.map((x) => (x.slot_id === id ? { ...x, private_notes: pn } : x)))}
    />
  );

  if (!cal.has_mentor) {
    return (
      <div>
        {header}
        {mentorBlock}
        <EmptyState>{t("You don't have an assigned mentor yet. Once one is assigned, their available sessions will appear here.")}</EmptyState>
      </div>
    );
  }

  const upcomingBookings = bookings.filter((b) => b.display_status !== 'completed' && b.display_status !== 'cancelled');
  const pastBookings = bookings.filter((b) => b.display_status === 'completed' || b.display_status === 'cancelled');

  return (
    <div>
      {header}
      {mentorBlock}

      {(courseId || ctxTitles.courseTitle) && (
        <div className={`${panelCls} mb-5 flex items-start gap-3 px-4 py-3`}>
          <BookOpen size={16} className="mt-0.5 shrink-0 text-lms-accent" />
          <div className="text-sm">
            <div className="font-medium">{t('Your booking will reference this lesson')}</div>
            <div className="text-lms-muted">
              {[ctxTitles.courseTitle, ctxTitles.lessonTitle].filter(Boolean).join(' › ') || t('The lesson you were studying')}
            </div>
          </div>
        </div>
      )}

      {!cal.can_book && (
        <div className="mb-5 rounded-lg border border-[var(--lms-warning)] bg-lms-surface px-4 py-3 text-sm">
          {t('Your membership level does not include booking mentor sessions. You can still see the available times.')}
        </div>
      )}

      {upcomingBookings.length > 0 && (
        <section className="mb-7">
          <h2 className="mb-2.5 text-sm font-semibold">{t('My sessions')}</h2>
          <div className="space-y-2">
            {upcomingBookings.map((b) => (
              <BookingRow key={b.slot_id} b={b} onCancel={() => cancel(b.slot_id)} />
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="mb-2.5 text-sm font-semibold">{t('Available sessions')}</h2>
        {days.length === 0 ? (
          <EmptyState>{t('Your mentor has no sessions open for course questions right now. Check back soon.')}</EmptyState>
        ) : (
          <div className="space-y-5">
            {days.map(([date, slots]) => (
              <div key={date}>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-lms-muted">{dayLabel(date)}</div>
                <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
                  {slots.map((s) => (
                    <SlotCard key={s.id} slot={s} cal={cal} onBook={() => openBook(s)} onCancel={() => cancel(s.id)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {pastBookings.length > 0 && (
        <section className="mt-7">
          <h2 className="mb-2.5 text-sm font-semibold text-lms-muted">{t('Past sessions')}</h2>
          <div className="space-y-2">
            {pastBookings.map((b) => (
              <BookingRow key={b.slot_id} b={b} />
            ))}
          </div>
        </section>
      )}

      {dialog && (
        <Modal title={t('Book a session')} onClose={() => !busy && setDialog(null)}>
          <div className="space-y-4">
            <div className="text-sm">
              <div className="font-medium">{dialog.title || dialog.session_type}</div>
              <div className="text-lms-muted">
                {dayLabel(dialog.date)} · {dialog.start_time} – {dialog.end_time}
              </div>
            </div>
            <Field label={t('What would you like to go over?')} hint={t('Optional — helps your mentor prepare.')}>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                maxLength={1000}
                className={inputCls}
                placeholder={t('e.g. I did not understand the second example of this lesson…')}
              />
            </Field>
            {cal.paid_enabled && (dialog.price_cents || 0) > 0 && (
              <p className="flex items-center gap-1.5 text-xs text-lms-muted">
                <CreditCard size={13} /> {t('One session credit will be used.')}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setDialog(null)} disabled={busy} className={`${btnGhost} ${press}`}>
                {t('Cancel')}
              </button>
              <button type="button" onClick={confirmBook} disabled={busy} className={`${btnPrimary} ${press}`}>
                {busy ? t('Booking…') : isFull(dialog) ? t('Join waiting list') : t('Confirm booking')}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

const isFull = (s) => (s.booked_count || 0) >= (s.max_students || 1);

function SlotCard({ slot, cal, onBook, onCancel }) {
  const max = slot.max_students || 1;
  const left = Math.max(0, max - (slot.booked_count || 0));
  const paid = cal.paid_enabled && (slot.price_cents || 0) > 0;
  const full = isFull(slot);

  let action;
  if (slot.my_status === 'booked') {
    action = (
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-[var(--lms-success)]">{t('Booked')}</span>
        <button type="button" onClick={onCancel} className={`text-xs text-lms-muted hover:text-[var(--lms-danger)] ${press}`}>
          {t('Cancel')}
        </button>
      </div>
    );
  } else if (slot.my_status === 'waitlist') {
    action = (
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-[var(--lms-info)]">{t('On the waiting list')}</span>
        <button type="button" onClick={onCancel} className={`text-xs text-lms-muted hover:text-[var(--lms-danger)] ${press}`}>
          {t('Leave')}
        </button>
      </div>
    );
  } else if (!cal.can_book) {
    action = null;
  } else if (paid && !cal.credits_available && !full) {
    action = (
      <a href={cal.my_account_url} target="_blank" rel="noopener noreferrer" className={`${btnGhost} ${press} w-full justify-center`}>
        {t('Pay & book in My Account')} <ExternalLink size={13} />
      </a>
    );
  } else {
    action = (
      <button type="button" onClick={onBook} className={`${full ? btnGhost : btnPrimary} ${press} w-full justify-center`}>
        {full ? t('Join waiting list') : paid ? t('Book with 1 credit') : t('Book this session')}
      </button>
    );
  }

  return (
    <div className={`${panelCls} flex flex-col gap-2.5 p-3.5`} data-testid={`mentor-slot-${slot.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 text-sm font-semibold lms-num">
          <Clock size={14} className="text-lms-accent" /> {slot.start_time} – {slot.end_time}
        </div>
        {paid && <span className="text-xs font-medium text-lms-muted lms-num">{money(slot.price_cents, slot.currency)}</span>}
      </div>
      <div className="text-sm">{slot.title || slot.session_type}</div>
      <div className="flex items-center gap-1.5 text-xs text-lms-muted">
        <Users size={13} />
        {full ? t('Full') : `${left} / ${max} ${t('spots left')}`} · {slot.session_type}
      </div>
      {slot.virtual_link && slot.my_status === 'booked' && (
        <a href={slot.virtual_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs font-medium text-lms-accent hover:underline">
          <Video size={13} /> {t('Join the session')}
        </a>
      )}
      {action && <div className="mt-auto pt-1">{action}</div>}
    </div>
  );
}

function BookingRow({ b, onCancel }) {
  const ctx = b.lms_context || {};
  const where = [ctx.course_title, ctx.lesson_title].filter(Boolean).join(' › ');
  return (
    <div className={`${panelCls} flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3 text-sm`}>
      <CalendarClock size={15} className="text-lms-accent" />
      <div className="min-w-[12rem] flex-1">
        <div className="font-medium">
          {dayLabel(b.date)} · <span className="lms-num">{b.start_time} – {b.end_time}</span>
        </div>
        {(where || ctx.note) && (
          <div className="mt-0.5 text-xs text-lms-muted">
            {where}
            {where && ctx.note ? ' — ' : ''}
            {ctx.note && <span className="italic">“{ctx.note}”</span>}
          </div>
        )}
      </div>
      <span className="text-xs text-lms-muted">{t(STATUS_TEXT[b.display_status] || b.display_status)}</span>
      {b.virtual_link && b.display_status === 'upcoming' && (
        <a href={b.virtual_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs font-medium text-lms-accent hover:underline">
          <Video size={13} /> {t('Join')}
        </a>
      )}
      {onCancel && (
        <button type="button" onClick={onCancel} className={`text-xs text-lms-muted hover:text-[var(--lms-danger)] ${press}`}>
          {t('Cancel')}
        </button>
      )}
    </div>
  );
}

// ── Mentor side (s150) ──────────────────────────────────────────────────────
// The sessions I mentor in the LMS: who booked, what they want to go over (the
// member's note, read-only) and MY private notes — never shown to the member.
function MentorSessions({ sessions, onSaved }) {
  const today = todayStr();
  const upcoming = sessions.filter((s) => s.date >= today).reverse();
  const past = sessions.filter((s) => s.date < today);
  return (
    <section className="mb-7" data-testid="mentor-own-sessions">
      <h2 className="mb-1 text-sm font-semibold">{t('Sessions I mentor')}</h2>
      <p className="mb-2.5 text-xs text-lms-muted">{t('Your private notes are visible only to you.')}</p>
      <div className="space-y-2">
        {[...upcoming, ...past].map((s) => (
          <MentorSessionRow key={s.slot_id} s={s} past={s.date < today} onSaved={onSaved} />
        ))}
      </div>
    </section>
  );
}

function MentorSessionRow({ s, past, onSaved }) {
  const saved = s.private_notes?.notes || '';
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(saved);
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(saved), [saved]);
  const booked = (s.participants || []).filter((p) => p.status !== 'waitlist');

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await mentorAPI.saveNotes(s.slot_id, draft);
      onSaved(s.slot_id, data);
      toast.success(t('Private notes saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Could not save the notes'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`${panelCls} ${past ? 'opacity-80' : ''}`} data-testid={`mentor-own-session-${s.slot_id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left text-sm"
      >
        <CalendarClock size={15} className="text-lms-accent" />
        <span className="min-w-[12rem] flex-1">
          <span className="font-medium">
            {dayLabel(s.date)} · <span className="lms-num">{s.start_time} – {s.end_time}</span>
          </span>
          <span className="block text-xs text-lms-muted">
            {s.title || s.session_type} · {booked.length} {booked.length === 1 ? t('booking') : t('bookings')}
          </span>
        </span>
        {saved && <Lock size={13} className="text-lms-muted" aria-label={t('Has private notes')} />}
        <ChevronDown
          size={15}
          className="text-lms-muted transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </button>
      {open && (
        <div className="space-y-3 border-t border-lms-line px-4 py-3">
          {booked.length > 0 ? (
            <ul className="space-y-1.5 text-sm">
              {booked.map((p, i) => {
                const ctx = p.lms_context || {};
                const where = [ctx.course_title, ctx.lesson_title].filter(Boolean).join(' › ');
                return (
                  <li key={i}>
                    <span className="font-medium">{p.name}</span> <span className="text-xs text-lms-muted">{p.membership_id}</span>
                    {(where || ctx.note) && (
                      <div className="mt-1.5 rounded-md border-l-[3px] border-lms-accent bg-lms-raised px-3 py-2">
                        {where && (
                          <div className="mb-1">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-lms-muted">{t('Lesson')}</div>
                            <div className="text-sm">{where}</div>
                          </div>
                        )}
                        {ctx.note && (
                          <div>
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-lms-muted">{t('Notes')}</div>
                            <div className="whitespace-pre-line break-words text-sm leading-relaxed">{ctx.note}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-xs text-lms-muted">{t('No bookings yet.')}</p>
          )}
          <Field
            label={t('My private notes')}
            hint={t('Only you can see these notes — use them before, during or after the session.')}
          >
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={5}
              maxLength={20000}
              className={inputCls}
              placeholder={t('Agenda, what was covered, next steps…')}
              data-testid={`mentor-notes-${s.slot_id}`}
            />
          </Field>
          <div className="flex justify-end">
            <button type="button" onClick={save} disabled={saving || draft === saved} className={`${btnPrimary} ${press}`}>
              {saving ? t('Saving…') : t('Save notes')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
